import { omit } from 'ramda';
import classnames from 'classnames';
import { Tooltip } from '@wix/wix-base-ui';
import React, { CSSProperties, FC, useRef } from 'react';
import { LabelMode, PresetSectionItem } from '@wix/add-panel-common';
import { useGetFallbackImage } from '../../../../hooks/useGetFallbackImage';
import { useUtils } from '../../../../UtilsProvider';
import { PresetAreaItem } from './PresetAreaItem';
import { PresetItem } from './PresetItem';
import styles from './PresetSection.scss';
import type { PresetSectionProps } from './PresetSection.types';

const DEFAULT_SCALING = 1;

const getMaxDimensions = (items: PresetSectionItem[]) =>
  items.reduce<{ width: number; height?: number }>(
    (res, item) => {
      const { rect } = item.preset;
      // res.width = Math.max(res.width, rect.x + rect.width);
      const height = rect.y + rect.height;
      if (typeof height === 'number' && !isNaN(height)) {
        res.height = Math.max(res.height ?? 0, height);
      }
      return res;
    },
    { width: 324, height: undefined },
  );

const getSectionDimensions = (dims: { width?: number; height?: number }, scaling: number): CSSProperties => {
  const style: CSSProperties = {};

  if (typeof dims.width === 'number') {
    style.width = dims.width * scaling;
  }

  if (typeof dims.height === 'number') {
    style.height = dims.height * scaling;
  }

  return style;
};

export const PresetSection: FC<PresetSectionProps> = ({
  section,
  index: sectionIndex,
  selectedItemKey,
  checkItemSelected,
  selectedItemSymbolName = '',
  shouldDisableTooltipOnHover = false,
}) => {
  const {
    media: { getMediaUrl },
    translate,
  } = useUtils();

  const {
    props,
    isImageSection,
    systemId,
    isDisabled,
    additionalBehaviours,
    liveTextSection,
    additionalScaling,
    automationId,
  } = section;
  const { items, image, retinaImage, imageStyle: _imageStyle } = props;

  // even though `width` and `height` don't exist in `imageStyle` type, we omit them, just to be on safe side
  const imageStyle = omit(['width', 'height'], _imageStyle);

  const { labelMode } = additionalBehaviours;
  const hasItems = Boolean(items && items.length > 0);

  const isShape = hasItems && Boolean(items[0].preset.shape);

  const sectionImageRef = useRef(null);

  const ItemComponent = isShape ? PresetAreaItem : PresetItem;
  const presetItems = hasItems
    ? items.map((item, index) => (
        <ItemComponent
          key={item.systemId}
          item={item}
          section={section}
          sectionImageRef={sectionImageRef}
          sectionIndex={sectionIndex}
          index={index}
          checkItemSelected={checkItemSelected}
          isSelected={selectedItemKey === item.id}
          selectionIndicationSymbolName={selectedItemSymbolName}
        />
      ))
    : [];

  const getImageDimensions = () => {
    if (isImageSection || liveTextSection) {
      //not in cms!!!
      return {};
    }
    const maxDimensions = getMaxDimensions(items);

    return {
      width: maxDimensions.width,
      height: maxDimensions.height,
    };
  };

  const canShowLabel = labelMode !== LabelMode.None;

  const imageDimensions = getImageDimensions();

  const wrapperStyle = getSectionDimensions(imageDimensions, additionalScaling || DEFAULT_SCALING);

  const getSectionItemsStyle = () => {
    const style: CSSProperties = {};

    if (additionalScaling && !isImageSection) {
      const scale = `scale(${additionalScaling},${additionalScaling})`;
      const transformOrigin = '0 0';
      const backfaceVisibility = 'hidden';
      style.WebkitTransform = scale;
      style.msTransform = scale;
      style.transform = scale;
      style.msTransformOrigin = transformOrigin;
      style.WebkitTransformOrigin = transformOrigin;
      style.transformOrigin = transformOrigin;
      style.WebkitBackfaceVisibility = backfaceVisibility;
      style.backfaceVisibility = backfaceVisibility;
    }

    return style;
  };

  const getFallbackImage = useGetFallbackImage();

  const sectionImageSrc = image && getMediaUrl(image, getFallbackImage(image));
  const sectionRetinaImageSrc = retinaImage && getMediaUrl(retinaImage, getFallbackImage(retinaImage));

  return (
    <div
      className={classnames(styles.itemsWrapper, isImageSection && styles.imageItemsWrapper)}
      style={wrapperStyle}
      data-hook={automationId}
    >
      <Tooltip
        content={translate('add_section_tooltip_drag')}
        shouldTranslate={false}
        openDelay={300}
        positionByMouse
        alignment="RIGHT"
        disabled={shouldDisableTooltipOnHover}
        dataHook="drag-and-drop-tooltip"
      >
        <div
          className={classnames(
            styles.items,
            isDisabled && styles.disabledPresetSection,
            canShowLabel && styles.hasLabel,
          )}
          style={getSectionItemsStyle()}
        >
          {(sectionImageSrc || sectionRetinaImageSrc) && (
            <div
              style={{
                maxHeight: imageDimensions.height, //cropping image height to section height, (because somtimes images are taller and overflows bottom section. e.g. community -> recent posts)
                maxWidth: imageDimensions.width,
              }}
            >
              <picture>
                {sectionRetinaImageSrc && <source srcSet={`${sectionRetinaImageSrc} 2x`} />}
                {sectionImageSrc && <source srcSet={`${sectionImageSrc} 1x`} />}
                <img
                  style={imageStyle}
                  ref={sectionImageRef}
                  src={sectionImageSrc}
                  alt=""
                  useMap={isShape ? `#${systemId}` : ''}
                />
              </picture>
            </div>
          )}

          <div className={classnames({ [styles.presetItemMargin]: !image })}>
            {isShape ? <map name={systemId}>{presetItems}</map> : presetItems}
          </div>
        </div>
      </Tooltip>
    </div>
  );
};
